import React, { useEffect, useRef, useState } from 'react';
import { AsyncSelect } from '@creditflowresearch/uilib/Select';
import FilterSection from '@/creditflowresearch/ui/common/components/organisms/Filters/components/FilterSection';
import Logger from '@framework/sentry/Logger';

interface ISelected {
  value: string,
  label: string,
}

interface IAsyncList<T> {
  sectionId: number | string,
  sectionTitle: string,
  isVisible: boolean,
  name: string,
  placeholder: string,
  dataTest: string,
  filterName: string,
  selectedValues: string[],
  onSectionToggle: (activeKey: number | string) => void,
  handleFilterUpdate: (filters) => void,
  loadOptions: (search: string) => Promise<{ allowed: T[], selected: T[] }>,
  optionsMapper: (any) => any,
  labelField?: string,
}

const DEBOUNCE_TIMEOUT = 250;

const AsyncList = <T extends { value: string, label: any }>({
  sectionId,
  sectionTitle,
  isVisible,
  name,
  placeholder,
  dataTest,
  filterName,
  selectedValues,
  onSectionToggle,
  handleFilterUpdate,
  loadOptions,
  optionsMapper,
  labelField,
}: IAsyncList<T>) => {
  const [options, setOptions] = useState<T[]>([]);
  const [selected, setSelected] = useState<ISelected[]>([]);
  const prevSelectValuesRef = useRef<string[]>([]);

  const handleChange = (selectedValues: ISelected[]) => {
    const newSelected = selectedValues.map((item) => {
      const option = options.find((option) => item.value === option.value);

      return {
        label: option[labelField] || option.label,
        value: item.value,
      };
    });

    handleFilterUpdate({ [filterName]: selectedValues.map((item) => item.value) });
    setSelected(newSelected);
  };

  const fetchOptions = async (inputValue: string): Promise<T[]> => {
    try {
      const newOptions = await loadOptions(inputValue);

      const allowedOptions = newOptions.allowed.map(optionsMapper);
      const selectedOptions = newOptions.selected.map(optionsMapper);
      const allOptions = [...allowedOptions, ...selectedOptions];

      setOptions(allOptions);
      return allOptions;
    } catch (error) {
      Logger.error({ message: 'Failed to load async filter options', error });
      return [];
    }
  };

  const onLoadOptions = (inputValue: string, callback: (options: T[]) => void) => {
    fetchOptions(inputValue).then(callback);
  };

  useEffect(() => {
    fetchOptions('');
  }, []);

  useEffect(() => {
    const prevSelectLength = prevSelectValuesRef.current.length;
    const selectLength = selectedValues.length;

    if (prevSelectLength === 0 && selectLength > 0 && selected.length === 0) {
      fetchOptions('').then((data) => {
        const newSelected = selectedValues.map((id) => {
          const option = data.find((option) => id === option.value);

          return {
            value: id,
            label: option ? (option[labelField] || option.label) : id,
          };
        });

        setSelected(newSelected);
      });
    } else if (prevSelectLength !== 0 && selectLength === 0 && selected.length !== 0) {
      fetchOptions('').then(() => {
        setSelected([]);
      });
    }
  }, [selectedValues, selected]);

  useEffect(() => {
    prevSelectValuesRef.current = selectedValues;
  }, [selectedValues]);

  return (
    <FilterSection
      sectionId={ sectionId }
      headerComponent={ sectionTitle }
      isVisible={ isVisible }
      onSectionToggle={ onSectionToggle }
    >
      <AsyncSelect
        name={ name }
        value={ selected }
        onChange={ handleChange }
        placeholder={ placeholder }
        defaultOptions={ options }
        options={ options }
        isMulti
        isNarrow
        isSupportHighlighting={ false }
        loadOptions={ onLoadOptions }
        dataTest={ dataTest }
        useDebounce={ { timeout: DEBOUNCE_TIMEOUT } }
      />
    </FilterSection>
  );
};

export default AsyncList;
